import { Syringe, FlaskConical, ClipboardList, MessageSquareText, CalendarDays } from 'lucide-react';
import { vaOr, BB_DOC } from './vaOr';

/*
 * VA Regional rotation. Built from the Blackboard VAOR Regional Orientation Guide; the
 * full-length regional and weekend orientations are linked. See docs/blackboard-va.md.
 * Contacts, Locations and Documents extend the General OR rotation.
 */

const REGIONAL = BB_DOC('_255085_1');
const WELCOME = BB_DOC('_255083_1');

export const vaRegional = {
  accent: vaOr.accent,
  icon: Syringe,
  tagline: 'Blocks from 07:00, catheters, follow-up calls',
  notesKey: 'notes:va:regional',

  toolSections: [
    { title: 'Clinical tools', tools: [
      { id: 'blocks', title: 'Block Day', subtitle: 'Before, solutions, after', icon: FlaskConical, action: 'guide:blocks', featured: true },
      { id: 'followup', title: 'Follow-Up', subtitle: 'Inpatients, outpatients, catheters, weekends', icon: ClipboardList, action: 'guide:followup' },
      { id: 'instructions', title: 'Patient Instructions', subtitle: 'What to tell them before they go', icon: MessageSquareText, action: 'guide:instructions' },
    ] },
    { title: 'Logistics', tools: [
      { id: 'day', title: 'Your Day', subtitle: 'The pager, the schedule, access', icon: CalendarDays, action: 'guide:day' },
    ] },
  ],

  contacts: {
    storageKey: 'va-regional-favorites',
    groups: [
      { id: 'regional', title: 'Regional', open: true, items: [
        { label: 'Regional pager', phone: '2812622795', note: 'From the call CRNA in the morning, to the call attending at 15:30' },
      ] },
      ...vaOr.contacts.groups,
    ],
  },

  locations: {
    groups: [
      { id: 'regional', title: 'Regional', items: [
        { name: 'Block bays', floor: 'PACU', where: 'Spots 8 and 9' },
        { name: 'First block cases', floor: 'SACC, 5H', note: 'Consent them here, then bring the patient to holding yourself once the SACC nurse agrees' },
        { name: 'Pump key', floor: 'Anesthesia workroom Omnicell', note: 'Also in the SICU and 5A' },
      ] },
      ...vaOr.locations.groups,
    ],
  },

  documents: {
    storageKey: 'va-regional-pinned-docs',
    groups: [
      { id: 'regional', title: 'Regional', items: [
        { title: 'Regional orientation', subtitle: 'Blackboard', url: REGIONAL },
        { title: 'Full regional resident orientation', subtitle: 'Blackboard', url: WELCOME },
        { title: 'Weekend regional duties', subtitle: 'Blackboard', url: WELCOME },
      ] },
      ...vaOr.documents.groups,
    ],
  },

  guides: {
    blocks: {
      title: 'Block Day',
      sections: [
        { title: 'Before the block', blocks: [{ type: 'bullets', items: [
          'Preop, labs, surgical and anesthesia consents, surgical site marked, IV, monitors',
          'Block site marked as well',
          'Sedation with midazolam and fentanyl, sized to the patient',
        ] }] },
        { title: 'Solutions', blocks: [{ type: 'rows', rows: [
          { label: 'Ropivacaine 0.5%', value: 'Epinephrine 1:400,000' },
          { label: 'Bupivacaine 0.5%', value: 'Epinephrine 1:400,000' },
          { label: 'Mepivacaine 1.5%', value: 'Epinephrine 1:200,000' },
        ] }, { type: 'bullets', items: [
          { text: 'No epinephrine for ankle, wrist or digital blocks', warn: true },
          'Everything labelled and locked in the cart',
        ] }] },
        { title: 'After the block', blocks: [{ type: 'steps', items: [
          'Monitor for 30 minutes or more. Watch for LAST and oversedation',
          'Add the patient to the block and epidural list: My Computer, Services, Anesthesiology, Regional Anesthesia',
          'Procedure note, and orders if needed. Instructions are in the Regional Anesthesia folder',
        ] }] },
      ],
      sources: [{ title: 'VAOR regional orientation guide', href: REGIONAL, detail: 'Blackboard, sign-in required' }],
    },

    followup: {
      title: 'Follow-Up',
      sections: [
        { title: 'Inpatients', blocks: [{ type: 'bullets', items: [
          'Catheters daily. Single-shot blocks on day one',
          'Note, cosigned by the attending',
          'Pulling a catheter: gently, check the tip is intact, write a note',
          'Epidural catheters come out only after you have spoken to the attending',
        ] }] },
        { title: 'Outpatients', blocks: [{ type: 'bullets', items: [
          'Catheter patients: call daily. No answer: voicemail asking them to call back',
          'Single shots: call on day one. Voicemail is fine; reaching them is not essential',
          'Document the call in a note, cosigned',
        ] }] },
        { title: 'Weekends', blocks: [{ type: 'bullets', items: [
          'Before the weekend: Omnicell, Innovian, CPRS e-signature, badge access to the OR and Omnicell rooms, the Services drive and Anesthesiology folder',
          'The weekend regional attending makes every management decision',
          'Round on all inpatient catheters and epidurals; call every outpatient catheter and day-one single shot. Notes cosigned',
          'Pump, bolus button and AC adapter back to the anesthesia workroom. Opioid cartridges are wasted with a nurse',
        ] }] },
      ],
      sources: [{ title: 'VAOR regional orientation guide', href: REGIONAL, detail: 'Blackboard, sign-in required' }],
    },

    instructions: {
      title: 'Patient Instructions',
      sections: [
        { title: 'Without a catheter', blocks: [{ type: 'bullets', items: [
          'Pain comes back 10–15 hours after surgery',
          'Take the oral pain medicine as soon as the block starts wearing off. Stay ahead of it',
        ] }] },
        { title: 'With a catheter', blocks: [{ type: 'bullets', items: [
          'Press the button for an extra dose and wait 15 minutes. It only works every 30 minutes',
          'Still hurting: oral pain medicine per the discharge instructions',
        ] }] },
        { title: 'Do', blocks: [{ type: 'bullets', items: [
          'Ask for help moving. Crutches or a wheelchair',
          'Arm in the sling or splint except for physical therapy',
        ] }] },
        { title: 'Do not', blocks: [{ type: 'bullets', items: [
          'Drive or operate machinery while the block is working',
          'Put any weight on the blocked leg',
        ] }] },
      ],
      sources: [{ title: 'VAOR regional orientation guide', href: REGIONAL, detail: 'Blackboard, sign-in required' }],
    },

    day: {
      title: 'Your Day',
      sections: [
        { title: 'Routine', blocks: [{ type: 'bullets', items: [
          'The night before: look at tomorrow’s schedule for block candidates',
          'Pick up the regional pager from the call CRNA in the morning. Blocks start by 07:00 at the latest',
          'Consent the first block cases in SACC. When the SACC nurse clears them, walk the patient to holding yourself',
          'Hand the pager to the call attending at 15:30',
        ] }] },
      ],
      sources: [{ title: 'VAOR regional orientation guide', href: REGIONAL, detail: 'Blackboard, sign-in required' }],
    },
  },
};
